import {Subject, timer} from 'rxjs';
import {repeatWhen, takeUntil} from 'rxjs/operators';
import {TimeInForce, OrderSide, OrderType} from '@qep/types/src/index';

export * from './math';
interface CreateIntervalOptions {
  initialDelay?: number;
  repeatDelay?: number;
}

export function timeInForceToText(tif: string | number) {
  switch (Number(tif)) {
    case TimeInForce.TIMEINFORCE_REGULAR_TRADING_HOURS: {
      return 'Day';
    }

    case TimeInForce.TIMEINFORCE_UNTIL_CANCELED: {
      return 'GTC';
    }
  }

  return '';
}

export function orderTypeToText(orderType: string | number) {
  switch (Number(orderType)) {
    case OrderType.ORDERTYPE_LIMIT: {
      return 'Limit';
    }

    case OrderType.ORDERTYPE_MARKET: {
      return 'Market';
    }

    case OrderType.ORDERTYPE_STOP: {
      return 'Stop';
    }
  }

  return '';
}

export function orderSideToText(orderSide: string | number) {
  switch (Number(orderSide)) {
    case OrderSide.ORDERSIDE_BUY: {
      return 'Buy';
    }

    case OrderSide.ORDERSIDE_SELL: {
      return 'Sell';
    }
  }

  return '';
}

export function createInterval(
  callback: () => void,
  {initialDelay = 0, repeatDelay = 100}: CreateIntervalOptions,
) {
  const start = new Subject<void>();
  const stop = new Subject<void>();

  timer(initialDelay, repeatDelay)
    .pipe(
      takeUntil(stop),
      repeatWhen(() => start),
    )
    .subscribe(() => callback());

  stop.next();

  return {
    start: () => start.next(),
    stop: () => stop.next(),
  };
}

const isNumber = function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
};

export function formatNumber(num: unknown, decimalPlace = 2, abs?: boolean): string {
  if (!isNumber(num)) {
    if (num === null || num === undefined) {
      return '';
    }

    return String(num);
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlace,
    maximumFractionDigits: decimalPlace,
  }).format(abs ? Math.abs(num) : num);
}

export function convertStringNumberToNumber(num: string | number) {
  if (typeof num === 'string') {
    // eslint-disable-next-line unicorn/prefer-string-replace-all -- TS lib target may not include replaceAll
    return Number(num.replace(/[^\d.-]+/g, ''));
  }

  return num;
}

export function fixedDecimalsWithCommas(params) {
  if ((!params.value && params.value !== 0) || params.value === '-') {
    return '-';
  }

  const decimals = params?.data?.symbol?.decimalPlaces ?? 2;
  return new Intl.NumberFormat('en-US', {minimumFractionDigits: decimals, maximumFractionDigits: decimals}).format(params.value);
}

export function formatCostWithCustomDecimalPlaces(num: number | string, decimalPlaces = 2, useNumberFormat = true) {
  if (useNumberFormat) {
    return formatNumber(Number(num), decimalPlaces);
  }

  return Number(num).toFixed(decimalPlaces);
}

export function formatCost(num: number | string, useNumberFormat = true) {
  if (useNumberFormat) {
    return formatNumber(Number(num), 2);
  }

  return Number(num).toFixed(2);
}

export function pick<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

export function mimicPriceFluctuation(
  originalPrice: number,
  fluctuationAmount: number,
  excludeNegatives = false,
) {
  if (excludeNegatives) {
    return originalPrice + Math.random() * fluctuationAmount;
  }

  return (
    originalPrice
    + Math.ceil(Math.random() * fluctuationAmount)
    * (Math.round(Math.random()) ? 1 : -1)
  );
}

export function numberAbbreviation(num: number, digits = 1) {
  const units = [
    {value: 1, symbol: ''},
    {value: 1e3, symbol: 'k'},
    {value: 1e6, symbol: 'M'},
    {value: 1e9, symbol: 'B'},
    {value: 1e12, symbol: 'T'},
  ];

  let unitIdx;

  // Set unitIdx as the index of the unit to use
  for (unitIdx = units.length - 1; unitIdx > 0; unitIdx--) {
    if (num >= units[unitIdx].value) {
      break;
    }
  }

  return (num / units[unitIdx].value).toFixed(digits) + units[unitIdx].symbol;
}

export function numberWithPadding(number: number | string, padding = 2) {
  return String(number).padStart(padding, '0');
}

export function safeChartData(data: number) {
  if (!Number.isFinite(data) || Number.isNaN(data)) {
    return 0;
  }

  return data || 0;
}

export const randomId = () => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);

export const tickSizeToDecimalPlaces = (tickSize: number | string) => {
  const tickSizeStr = Number(tickSize).toString();
  const decimalIndex = tickSizeStr.indexOf('.');
  return decimalIndex === -1 ? 0 : tickSizeStr.length - (decimalIndex + 1);
};

export interface RateLimiterQueueParams<T = unknown> {
  throttleTime?: number;
  keepMessages?: boolean;
  responseCallback: (response: T[]) => void;
}

/**
 * This class is used to throttle the rate of updates. It'll batch updates and send them in a single response after the specficied throttleTime has passed.
 * @todo move to shared
 * @param {RateLimiterQueueParams} params
 * @param {number} [params.throttleTime]  The time to wait before sending a response
 * @param {Function} params.responseCallback The callback to call when the throttleTime has passed
 * @param {boolean} [params.keepMessages] Whether to keep the messages in the queue after they've been sent--Not implemented yet
 */
export class RateLimiterQueue<T = unknown> {
  throttleTime: number;
  responseCallback: (response: T[]) => void;
  keepMessages: boolean;
  loopInterval: ReturnType<typeof setInterval> | undefined = undefined;
  queue: T[] = [];
  private isStopped = false;

  constructor(params: RateLimiterQueueParams<T>) {
    this.throttleTime = params?.throttleTime || 100;
    this.responseCallback = params.responseCallback;
    this.keepMessages = params?.keepMessages ?? true;
    this._startLoop();
  }

  private _startLoop() {
    if (this.loopInterval || this.isStopped) {
      return;
    }

    this.loopInterval = setInterval(() => {
      if (this.queue.length === 0) {
        return;
      }

      const items = this.queue.splice(0);
      if (items.length === 0) {
        return;
      }

      this.responseCallback(items);
    }, this.throttleTime);
  }

  rateLimit(item: T) {
    if (this.isStopped) {
      return;
    }

    this.queue.push(item);
  }

  /**
   * Synchronously discards matching pending items while preserving nonmatching
   * work in its existing order for the next throttled callback.
   * @param predicate Selects pending items that should be discarded.
   */
  removeWhere(predicate: (item: T) => boolean) {
    this.queue = this.queue.filter(item => !predicate(item));
  }

  stop({clearQueue = true}: {clearQueue?: boolean} = {}) {
    if (this.loopInterval) {
      clearInterval(this.loopInterval);
      this.loopInterval = undefined;
    }

    this.isStopped = true;
    if (clearQueue) {
      this.queue = [];
    }
  }

  dispose() {
    this.stop();
  }
}

export const convertSecondsToHHMM = (seconds = 0, padZeroes = true) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  let hoursString = `${hours.toString().padStart(2, '0')}:`;
  let minutesString = minutes.toString().padStart(2, '0');
  if (!padZeroes) {
    if (hours === 0) {
      hoursString = '';
    } else if (hours < 10) {
      hoursString = `${hours.toString()}:`;
    }

    if (hours === 0 && minutes === 0) {
      minutesString = '0';
    }
  }

  return `${hoursString}${minutesString}`;
};

export const convertSecondsToHHMMRoundUp = (seconds = 0, padZeroes = true) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds - hours * 3600) / 60);
  let hoursString = `${hours.toString().padStart(2, '0')}:`;
  let minutesString = minutes.toString().padStart(2, '0');
  if (!padZeroes) {
    if (hours === 0) {
      hoursString = '';
    } else if (hours < 10) {
      hoursString = `${hours.toString()}:`;
    }

    if (hours === 0 && minutes === 0) {
      minutesString = '0';
    }
  }

  return `${hoursString}${minutesString}`;
};

export const convertSecondsToHHMMSS = (seconds = 0, padZeroes = true) => {
  const date = new Date();
  date.setSeconds(seconds);
  return new Date(seconds * 1000).toISOString().slice(11, 19);

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  let hoursString = `${hours.toString().padStart(2, '0')}:`;
  let minutesString = minutes.toString().padStart(2, '0');
  if (!padZeroes) {
    if (hours === 0) {
      hoursString = '';
    } else if (hours < 10) {
      hoursString = `${hours.toString()}:`;
    }

    if (hours === 0 && minutes === 0) {
      minutesString = '0';
    }
  }

  return `${hoursString}${minutesString}`;
};

export const convertHHMMToSeconds = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours * 3600) + (minutes * 60);
};

export const parseDurationStringToHoursAndMinutes = (duration: string): {hours: number; minutes: number} => {
  const [hours, minutes] = duration.split(':');
  return {
    hours: Number(hours),
    minutes: Number(minutes),
  };
};

const isoDateTimeWithoutTimezonePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
const timeOnlyPattern = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const timezoneSuffixPattern = /(?:z|[+-]\d{2}:?\d{2})$/i;

/**
 * Detects whether an API date-time string already carries UTC or offset information.
 */
function hasTimezoneSuffix(value: string): boolean {
  return timezoneSuffixPattern.test(value);
}

/**
 * Keeps UTC API date-time strings without suffixes from being parsed as browser-local time.
 */
function normalizeUtcDateString(value: string): string {
  const trimmedValue = value.trim();
  if (hasTimezoneSuffix(trimmedValue) || !isoDateTimeWithoutTimezonePattern.test(trimmedValue)) {
    return trimmedValue;
  }

  return `${trimmedValue}Z`;
}

/**
 * Parses UTC HH:mm values returned by modify details without interpreting them as local draft input.
 */
function parseUtcTimeOnly(value: string): Date | undefined {
  const match = timeOnlyPattern.exec(value.trim());
  if (!match) {
    return undefined;
  }

  const [, hoursText, minutesText, secondsText = '0'] = match;
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  const seconds = Number(secondsText);
  if (
    !Number.isInteger(hours)
    || !Number.isInteger(minutes)
    || !Number.isInteger(seconds)
    || hours < 0
    || hours > 23
    || minutes < 0
    || minutes > 59
    || seconds < 0
    || seconds > 59
  ) {
    return undefined;
  }

  const referenceDate = new Date();
  return new Date(Date.UTC(
    referenceDate.getUTCFullYear(),
    referenceDate.getUTCMonth(),
    referenceDate.getUTCDate(),
    hours,
    minutes,
    seconds,
  ));
}

/**
 * Parses API UTC date strings and UTC time-only fallbacks used by modify comparison.
 */
function parseUtcDateString(value: string): Date {
  return parseUtcTimeOnly(value) ?? new Date(normalizeUtcDateString(value));
}

export class Time {
  _value: Date;

  constructor(value: string | number | Date = Number.NaN) {
    this._value = new Date(value);
  }

  valueOf(): number {
    return this._value.getTime();
  }

  getTime(): number {
    return this._value.getTime();
  }

  isValid(): boolean {
    return !Number.isNaN(this._value.getTime());
  }

  hhmm(): string | undefined {
    if (!this.isValid()) {
      return undefined;
    }

    const hours = this._value.getHours();
    const minutes = this._value.getMinutes();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  hhmmss(): string | undefined {
    if (!this.isValid()) {
      return undefined;
    }

    const hours = this._value.getHours();
    const minutes = this._value.getMinutes();
    const seconds = this._value.getSeconds();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  add(duration: Duration): Time {
    return new Time(this._value.getTime() + duration.milliseconds());
  }

  subtract(duration: Duration): Time {
    return new Time(this._value.getTime() - duration.milliseconds());
  }

  equals(other: Time): boolean {
    return this._value.getTime() === other.getTime();
  }

  // Used if we know the string is UTC but it doesn't contain timezone offset
  static now(): Time {
    return new Time(new Date());
  }

  static fromUtcDateString(utcString: string | undefined): Time {
    if (!utcString) {
      return new Time();
    }

    return new Time(parseUtcDateString(utcString));
  }

  static fromHHMM(hhmm: string | undefined): Time {
    if (!hhmm) {
      return new Time();
    }

    const [hours, minutes] = hhmm.split(':');
    const date = dateAtTime(Number(hours), Number(minutes));
    return new Time(date);
  }
}

export class Duration {
  _ms: number;

  constructor(ms: number) {
    this._ms = ms;
  }

  isValid(): boolean {
    return this._ms > 0;
  }

  valueOf(): number {
    return this._ms;
  }

  milliseconds(): number {
    return this._ms;
  }

  seconds(): number {
    return this._ms / 1000;
  }

  minutes(): number {
    return this._ms / 1000 / 60;
  }

  hhmm(): string {
    if (!this.isValid()) {
      return '00:00';
    }

    return convertSecondsToHHMM(this.seconds());
  }

  hhmmss(): string {
    if (!this.isValid()) {
      return '00:00';
    }

    return convertSecondsToHHMMSS(this.seconds());
  }

  static between(start: Time, end: Time, enforceOrder = true): Duration {
    const diff = end.getTime() - start.getTime();
    const ms = enforceOrder ? diff : Math.abs(diff);
    return new Duration(ms);
  }

  static seconds(seconds: number): Duration {
    return new Duration(seconds * 1000);
  }

  static minutes(mins: number): Duration {
    return new Duration(mins * 60 * 1000);
  }

  static fromHHMM(hhmm: string): Duration {
    const [hours, minutes] = hhmm.split(':');
    return new Duration(Number(hours) * 60 * 60 * 1000 + Number(minutes) * 60 * 1000);
  }
}

export function dateAtTime(hours: number, minutes: number): Date {
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
}
